import { Alert, Button } from "@mui/material";
import { styled } from '@mui/material/styles';
import React, { useState } from "react";
import RefreshIcon from '@mui/icons-material/Refresh';
import ConfirmationDialog from "../../shared/ConfirmDialog";
import {DisplayDialog}  from "../DisplayDialog";
import { useDialogData } from "../../../hooks/useDialogData";
import { useSelector } from "react-redux";
import { RootState } from "../../../state/store";
import { useConfigurationData, ConfigurationDataType } from "../../../hooks/useConfigurationData";
import IntegrationService from "../../../services/integrationService";
import { IRefreshEncompassMappingsRequest } from "../../../models/IRefreshEncompassMappingsRequest";
import { styleTokens } from "../../../assets/styles/commonStyles";
import { DtConfirmDialog } from "../../ui/dialog";
import DtAlert from "../../ui/alert/DtAlert";

// Styled button to match header button style exactly
const StyledRefreshButton = styled(Button)(({ theme }) => ({
    color: theme.palette.common.white,
    borderRadius: theme.spacing(2),
    fontSize: '0.95rem',
    fontWeight: 500,
    textTransform: 'none',
    transition: styleTokens.transitions.default,
    backgroundColor: theme.palette.primary.main,

    '&:hover': {
        // Cyan hover tint (brand rule) with strong contrast text
        backgroundColor: 'rgba(0, 191, 232, 0.20)', // Cyan tint ~20%
        borderColor: 'rgba(0, 191, 232, 0.35)',
        color: theme.palette.common.white,
    },

    '&:active': {
        backgroundColor: 'rgba(4, 93, 139, 0.28)', // Venice tint for pressed
    },

    '&:focus-visible': {
        outline: `2px solid ${theme.palette.info.main}`, // Cyan focus ring
        outlineOffset: '2px',
    },

    '&:disabled': {
        color: 'rgba(255, 255, 255, 0.5)',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderColor: 'rgba(255, 255, 255, 0.1)',
    },
    
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.5, 1),
        fontSize: '0.65rem'
    },
    
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(0.5, 1.25)
    }
}));

interface RefreshMappingCacheButtonProps {
    onSuccess?: () => void;
    onError?: (error: any) => void;
}

const RefreshMappingCacheButton: React.FC<RefreshMappingCacheButtonProps> = ({ 
    onSuccess, 
    onError 
}) => {
    // State
    const [refreshingCache, setRefreshingCache] = useState(false);

    // Redux state
    const accessToken = useSelector((state: RootState) => state.appSlice.accessToken);
    const configId = useSelector((state: RootState) => state.appSlice.configId);
    
    // Configuration data for impersonation
    const configData = useConfigurationData(ConfigurationDataType.IMPERSONATION);

    const refreshDialogStates = useDialogData();
    const confirmationDialogStates = useDialogData();

    // Services
    const integrationService = new IntegrationService();

    // Button functions
    const handleRefreshMappingCache = () => { 
        refreshDialogStates.setDialogOpen(true);
        refreshDialogStates.setDialogTitle('Refresh Mapping Cache');
        refreshDialogStates.setDialogMessage('Are you sure you want to refresh the mapping cache?');
        refreshDialogStates.setDialogSubMessage('This operation may take a while to complete.');
    };

    const performRefreshMappingCache = async () => {
        try {
            setRefreshingCache(true);
            
            const payload: IRefreshEncompassMappingsRequest = {
                ConfigurationId: configId || undefined,
                ImpersonationOption: configData.data ? {
                    ClientCode: configData.data.ClientCode,
                    EnableImpersonation: configData.data.EnableImpersonation
                } : undefined
            };
            await integrationService.post(accessToken, 'refresh-epc-field-cache', payload);

            // Show success dialog
            confirmationDialogStates.setDialogTitle('Refresh Mapping Cache');
            confirmationDialogStates.setDialogMessage("Mapping cache has been refreshed successfully!");
            confirmationDialogStates.setDialogOpen(true);

            onSuccess?.();
            
        } catch (error) {
            console.error('Error refreshing mapping cache:', error);
            onError?.(error);
        } finally {
            setRefreshingCache(false);
        }
    };

    return (
        <>
            <StyledRefreshButton
                onClick={handleRefreshMappingCache}
                size='small'
                disabled={refreshingCache}
                startIcon={<RefreshIcon />}
            >
                {refreshingCache ? 'Refreshing...' : 'Mapping Cache'}
            </StyledRefreshButton>
            <DtConfirmDialog
                maxWidth="xs"
                open={refreshDialogStates.dialogOpen}
                title={refreshDialogStates.dialogTitle}
                message={refreshDialogStates.dialogMessage}
                subMessage={<DtAlert severity="warning">{refreshDialogStates.dialogSubMessage}</DtAlert>}
                onConfirm={() => {
                    refreshDialogStates.setDialogOpen(false);
                    performRefreshMappingCache();
                }}
                onClose={() => refreshDialogStates.setDialogOpen(false)}
            />
            <DisplayDialog
                open={confirmationDialogStates.dialogOpen}
                onClose={() => confirmationDialogStates.setDialogOpen(false)}
                closeButtonText="OK"
                title={confirmationDialogStates.dialogTitle}
                json={ confirmationDialogStates.dialogMessage }
            />
        </>
    );
};

export default RefreshMappingCacheButton;
